import * as React from "react";
import {
    DefaultButton,
    IconButton,
    mergeStyles,
    MessageBar,
    MessageBarButton,
    MessageBarType,
    Panel,
    PanelType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TooltipHost,
} from "@fluentui/react";
import { LanguageContext } from "../../utilities/LocalizationModule";
import { ICopilotPromptDto, ICopilotPromptInput } from "../../data-structures/interfaces";
import {
    copyGlobalCopilotPrompt,
    createTenantCopilotPrompt,
    deleteTenantCopilotPrompt,
    getTenantCopilotPrompts,
    getTenantLibraryNames,
    CopilotPromptCallResult,
    reorderTenantCopilotPrompts,
    setTenantCopilotPromptEnabled,
    setTenantCopilotPromptHidden,
    updateTenantCopilotPrompt,
} from "../../utilities/helpers/ApiHelper";
import {
    COPILOT_PROMPT_LIMITS,
    emptyCopilotPromptInput,
    formatString,
    movePromptIds,
    toCopilotPromptInput,
} from "../../utilities/helpers/copilotPrompts";
import ConfirmDialog from "../ConfirmDialog";
import { CopilotPromptEditor } from "./CopilotPromptEditor";
import { CopilotPromptList, copilotPromptMeta, copilotPromptMetaStyle, copilotPromptRowStyle } from "./CopilotPromptList";

export interface CopilotPromptsPanelProps {
    isOpen: boolean;
    /** Tenant.Id */
    tenantId: number;
    tenantName: string;
    onClose: () => void;
}

const hiddenPillStyle = mergeStyles({
    backgroundColor: "var(--db-pill-neutral-bg)",
    border: "1px solid var(--db-pill-neutral-border)",
    borderRadius: 10,
    color: "var(--db-pill-neutral-fg)",
    fontSize: 12,
    marginLeft: 8,
    padding: "0 8px",
});

/**
 * A tenant's Copilot prompts (spec 2026-10-13-copilot-chat-design.md §4 phase 3), opened from the
 * Copilot section's "Manage prompts".
 * - Docubird prompts: hide, show, copy to my prompts.
 * - Our prompts: new, edit, enable, move up/down, delete.
 * Every change reloads the list from the API, which owns the rules.
 */
export const CopilotPromptsPanel: React.FC<CopilotPromptsPanelProps> = ({ isOpen, tenantId, tenantName, onClose }) => {
    const strings = React.useContext(LanguageContext);
    const [prompts, setPrompts] = React.useState<ICopilotPromptDto[] | undefined>(undefined);
    const [loadFailed, setLoadFailed] = React.useState<boolean>(false);
    const [libraryNames, setLibraryNames] = React.useState<string[]>([]);
    const [busy, setBusy] = React.useState<boolean>(false);
    /** Why the last change failed (the API's text, or the generic message); undefined = none. */
    const [actionError, setActionError] = React.useState<string | undefined>(undefined);
    /** Another admin changed the list (a 404 or 409): it was reloaded. */
    const [wasReloaded, setWasReloaded] = React.useState<boolean>(false);
    /** The prompt being edited; null = a new prompt; undefined = the editor is closed. */
    const [editing, setEditing] = React.useState<ICopilotPromptDto | null | undefined>(undefined);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [saveError, setSaveError] = React.useState<string | undefined>(undefined);
    const [deleting, setDeleting] = React.useState<ICopilotPromptDto | null>(null);
    /** "Try again" after a failed first load counts up, which loads again. */
    const [loadAttempt, setLoadAttempt] = React.useState<number>(0);

    React.useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        let cancelled = false;
        setPrompts(undefined);
        setLoadFailed(false);
        getTenantCopilotPrompts(tenantId).then(list => {
            if (cancelled) return;
            // A failed load keeps prompts undefined: the panel then shows the error and "Try again"
            // only, never empty sections or "New prompt" (a tenant at 50 could not save anyway).
            setPrompts(list);
            setLoadFailed(!list);
        });
        return () => {
            cancelled = true;
        };
    }, [isOpen, tenantId, loadAttempt]);

    // The library suggestions load on their own: the template read and, for a tenant without a
    // template, up to 21 site calls must not hold the list (audits 2026-10-19, 2026-10-21). Until they arrive the editor suggests nothing; any name can be typed.
    React.useEffect(() => {
        if (!isOpen) {
            return undefined;
        }
        let cancelled = false;
        getTenantLibraryNames(tenantId).then(names => {
            if (!cancelled) setLibraryNames(names ?? []);
        });
        return () => {
            cancelled = true;
        };
    }, [isOpen, tenantId]);

    // The list when it was read; a failed reload keeps the list it had, and answers undefined.
    const reload = async (): Promise<ICopilotPromptDto[] | undefined> => {
        const list = await getTenantCopilotPrompts(tenantId);
        if (list) {
            setPrompts(list);
            setLoadFailed(false);
        } else {
            setLoadFailed(true);
        }
        return list ?? undefined;
    };

    // A 400 from create or copy is, in practice, the 50-prompt limit reached with a stale list, and
    // its text is the API's English (review 2026-10-20): the reloaded list tells whether it was the
    // limit; any other 400 gets the generic localized message.
    const refusalMessage = (list: ICopilotPromptDto[] | undefined, generic: string): string =>
        list && list.filter(prompt => !prompt.isGlobal).length >= COPILOT_PROMPT_LIMITS.tenantPrompts
            ? strings.CopilotPromptLimitReached
            : generic;

    // Runs one change, then reloads. A prompt another admin deleted or reordered meanwhile (404,
    // 409) only reloads, and says so; any other failure shows the API's reason or one message (a
    // copy's 400: refusalMessage).
    // When that reload fails too, the list is the old one: the change just failed (final review
    // 2026-10-19).
    const run = async (change: () => Promise<CopilotPromptCallResult<unknown>>, isCopy: boolean = false) => {
        setBusy(true);
        setActionError(undefined);
        setWasReloaded(false);
        try {
            const result = await change();
            const changedElsewhere = !result.ok && (result.status === 404 || result.status === 409);
            const reloaded = await reload();
            if (!result.ok && !changedElsewhere) {
                setActionError(isCopy && result.status === 400
                    ? refusalMessage(reloaded, strings.CopilotPromptActionFailed)
                    : result.message ?? strings.CopilotPromptActionFailed);
            }
            if (changedElsewhere) {
                if (reloaded) {
                    setWasReloaded(true);
                } else {
                    setActionError(strings.CopilotPromptActionFailed);
                }
            }
        } finally {
            setBusy(false);
        }
    };

    const globals = (prompts ?? []).filter(prompt => prompt.isGlobal);
    const own = (prompts ?? []).filter(prompt => !prompt.isGlobal);
    const atLimit = own.length >= COPILOT_PROMPT_LIMITS.tenantPrompts;

    const openEditor = (prompt: ICopilotPromptDto | null) => {
        setSaveError(undefined);
        setEditing(prompt);
    };

    const save = async (input: ICopilotPromptInput) => {
        setIsSaving(true);
        setSaveError(undefined);
        const saved = editing
            ? await updateTenantCopilotPrompt(tenantId, editing.id, input)
            : await createTenantCopilotPrompt(tenantId, input);
        if (saved.ok) {
            setIsSaving(false);
            setWasReloaded(false);
            setEditing(undefined);
            await reload();
            return;
        }
        // The prompt is gone (another admin deleted it): close and show the list as it is now. When
        // that list can't be read, the editor stays open with the save error (final review
        // 2026-10-19): "The list has been reloaded" would be untrue.
        if (saved.status === 404 && await reload()) {
            setIsSaving(false);
            setWasReloaded(true);
            setEditing(undefined);
            return;
        }
        if (!editing && saved.status === 400) {
            const list = await reload();
            setIsSaving(false);
            setSaveError(refusalMessage(list, strings.CopilotPromptSaveFailed));
            return;
        }
        setIsSaving(false);
        setSaveError(saved.message ?? strings.CopilotPromptSaveFailed);
    };

    return (
        <Panel
            headerText={`${strings.CopilotPromptsTitle} — ${tenantName}`}
            isOpen={isOpen}
            onDismiss={onClose}
            closeButtonAriaLabel={strings.Close}
            type={PanelType.medium}
            isLightDismiss={false}
        >
            {prompts === undefined ? (
                loadFailed ? (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        styles={{ root: { marginTop: 12 } }}
                        actions={<MessageBarButton onClick={() => setLoadAttempt(attempt => attempt + 1)}>{strings.TryAgain}</MessageBarButton>}
                    >
                        {strings.CopilotPromptsLoadFailed}
                    </MessageBar>
                ) : (
                    <Spinner size={SpinnerSize.large} styles={{ root: { marginTop: 40 } }} />
                )
            ) : (
                <Stack tokens={{ childrenGap: 20 }} styles={{ root: { marginTop: 12 } }}>
                    {loadFailed && <MessageBar messageBarType={MessageBarType.error}>{strings.CopilotPromptsLoadFailed}</MessageBar>}
                    {actionError && (
                        <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setActionError(undefined)}>
                            {actionError}
                        </MessageBar>
                    )}
                    {wasReloaded && (
                        <MessageBar messageBarType={MessageBarType.info} onDismiss={() => setWasReloaded(false)}>
                            {strings.CopilotPromptsReloaded}
                        </MessageBar>
                    )}

                    <Stack tokens={{ childrenGap: 8 }}>
                        <Text as="h3" variant="large">{strings.CopilotPromptsDocubird}</Text>
                        <Text className={copilotPromptMetaStyle}>{strings.CopilotPromptsDocubirdInfo}</Text>
                        {globals.length === 0 ? (
                            <Text>{strings.CopilotPromptsNone}</Text>
                        ) : (
                            globals.map(prompt => {
                                const visibility = prompt.isHidden ? strings.Show : strings.CopilotPromptHide;
                                const visibilityTip = prompt.isHidden ? strings.CopilotPromptShowTip : strings.CopilotPromptHideTip;
                                return (
                                    <Stack key={prompt.id} horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} className={copilotPromptRowStyle}>
                                        <Stack.Item grow styles={{ root: { minWidth: 0 } }}>
                                            <Text block>
                                                {prompt.title}
                                                {prompt.isHidden && <span className={hiddenPillStyle}>{strings.CopilotPromptHidden}</span>}
                                            </Text>
                                            <Text block variant="small" className={copilotPromptMetaStyle}>{copilotPromptMeta(prompt, strings, false)}</Text>
                                        </Stack.Item>
                                        {/* Icon buttons, as Our prompts' (Paul, 2026-10-16): the tooltip says what
                                            each does, and screen readers get it as the button's description. */}
                                        <TooltipHost content={visibilityTip} setAriaDescribedBy={false}>
                                            <IconButton
                                                iconProps={{ iconName: prompt.isHidden ? "View" : "Hide3" }}
                                                ariaLabel={`${visibility}: ${prompt.title}`}
                                                ariaDescription={visibilityTip}
                                                disabled={busy}
                                                onClick={() => run(() => setTenantCopilotPromptHidden(tenantId, prompt.id, !prompt.isHidden))}
                                            />
                                        </TooltipHost>
                                        <TooltipHost content={strings.CopilotPromptCopyTip} setAriaDescribedBy={false}>
                                            <IconButton
                                                iconProps={{ iconName: "Copy" }}
                                                ariaLabel={`${strings.CopilotPromptCopyToOwn}: ${prompt.title}`}
                                                ariaDescription={strings.CopilotPromptCopyTip}
                                                disabled={busy || atLimit}
                                                onClick={() => run(() => copyGlobalCopilotPrompt(tenantId, prompt.id), true)}
                                            />
                                        </TooltipHost>
                                    </Stack>
                                );
                            })
                        )}
                    </Stack>

                    <Stack tokens={{ childrenGap: 8 }}>
                        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                            <Text as="h3" variant="large">{strings.CopilotPromptsOwn}</Text>
                            <PrimaryButton text={strings.CopilotPromptNew} disabled={busy || atLimit} onClick={() => openEditor(null)} />
                        </Stack>
                        <Text className={copilotPromptMetaStyle}>{strings.CopilotPromptsOwnInfo}</Text>
                        {atLimit && <MessageBar messageBarType={MessageBarType.warning}>{strings.CopilotPromptLimitReached}</MessageBar>}
                        {own.length === 0 ? (
                            <Text>{strings.CopilotPromptsNone}</Text>
                        ) : (
                            <CopilotPromptList
                                prompts={own}
                                showAudience={false}
                                busy={busy}
                                onToggleEnabled={prompt => run(() => setTenantCopilotPromptEnabled(tenantId, prompt.id, !prompt.isEnabled))}
                                onMove={(index, delta) => {
                                    const ids = movePromptIds(own, index, delta);
                                    if (ids) run(() => reorderTenantCopilotPrompts(tenantId, ids));
                                }}
                                onEdit={openEditor}
                                onDelete={setDeleting}
                            />
                        )}
                    </Stack>
                </Stack>
            )}
            {editing !== undefined && (
                <CopilotPromptEditor
                    isGlobal={false}
                    isNew={editing === null}
                    initial={editing ? toCopilotPromptInput(editing) : emptyCopilotPromptInput(false)}
                    libraryNames={libraryNames}
                    isSaving={isSaving}
                    errorMessage={saveError}
                    onSave={save}
                    onCancel={() => setEditing(undefined)}
                />
            )}
            <ConfirmDialog
                isOpen={!!deleting}
                title={strings.Delete}
                message={deleting ? formatString(strings.CopilotPromptDeleteConfirm, deleting.title) : ""}
                confirmText={strings.Delete}
                onConfirm={async () => {
                    const target = deleting;
                    setDeleting(null);
                    if (target) await run(() => deleteTenantCopilotPrompt(tenantId, target.id));
                }}
                onDismiss={() => setDeleting(null)}
            />
        </Panel>
    );
};

export default CopilotPromptsPanel;
